import { useState } from "react";
import { servicesApi, servicesQueryKeys } from "../../../../shared/api/services";
import {
  treatmentPackagesApi,
  treatmentPackagesQueryKeys,
} from "../../../../shared/api/treatmentPackages";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { dentalApi, dentalQueryKeys, type DentalPlan } from "../../../../shared/api/dental";
import { ErrorState } from "../../../../shared/ui/ErrorState";

const ITEM_STATUS_LABEL: Record<string, string> = {
  PLANNED: "Chưa làm",
  IN_PROGRESS: "Đang làm",
  DONE: "Hoàn tất",
  CANCELLED: "Đã huỷ",
};

/** Multi-session dental treatment plans for a patient — create plan, add items, mark a session done. */
export function DentalTreatmentPlans({
  patientId,
  encounterId,
}: {
  patientId: string;
  encounterId?: string | null;
}) {
  const qc = useQueryClient();
  const plans = useQuery({
    queryKey: dentalQueryKeys.plans(patientId),
    queryFn: () => dentalApi.listPlans(patientId),
  });

  const [newTitle, setNewTitle] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: dentalQueryKeys.plans(patientId) });

  const createPlan = useMutation({
    mutationFn: (title: string) => dentalApi.createPlan(patientId, title),
    onSuccess: () => {
      setNewTitle("");
      invalidate();
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-56">
          <label className="mb-1 block text-sm font-medium">Tạo kế hoạch điều trị mới</label>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="VD: Điều trị tuỷ R36 + phục hình"
          />
        </div>
        <Button
          type="button"
          disabled={!newTitle.trim() || createPlan.isPending}
          onClick={() => createPlan.mutate(newTitle.trim())}
        >
          {createPlan.isPending ? "Đang tạo…" : "Tạo kế hoạch"}
        </Button>
      </div>
      {createPlan.isError && <ErrorState error={createPlan.error} inline />}

      {plans.isLoading && <p className="text-sm text-muted-foreground">Đang tải kế hoạch…</p>}
      {plans.isError && <ErrorState error={plans.error} />}
      {plans.data && plans.data.length === 0 && (
        <p className="text-sm text-muted-foreground">Chưa có kế hoạch điều trị nào.</p>
      )}

      {plans.data?.map((plan) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          patientId={patientId}
          encounterId={encounterId}
          onChanged={invalidate}
        />
      ))}
    </div>
  );
}

function PlanCard({
  plan,
  patientId,
  encounterId,
  onChanged,
}: {
  plan: DentalPlan;
  patientId: string;
  encounterId?: string | null;
  onChanged: () => void;
}) {
  const [tooth, setTooth] = useState("");
  const [desc, setDesc] = useState("");
  const [sessionNo, setSessionNo] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [patientPackageId, setPatientPackageId] = useState("");

  // #18869 — the HIS service catalog and the patient's own packages, not a dental-only copy of
  // either. Both endpoints already existed; the form simply never offered them, so the
  // package-session deduction that #18637 built could not be triggered from this screen.
  const services = useQuery({
    queryKey: servicesQueryKeys.all(),
    queryFn: () => servicesApi.listAll(),
    staleTime: 5 * 60_000,
  });
  const packages = useQuery({
    queryKey: treatmentPackagesQueryKeys.ofPatient(patientId),
    queryFn: () => treatmentPackagesApi.forPatient(patientId),
    staleTime: 60_000,
  });
  // `redeemable` is the server's answer, computed beside the guards `redeem` enforces. Filtering
  // on status/expiry/remaining here would be a second copy of those rules, free to drift.
  const usablePackages = (packages.data ?? []).filter((p) => p.redeemable);

  const addItem = useMutation({
    mutationFn: () =>
      dentalApi.addPlanItem(plan.id, {
        toothFdi: tooth.trim() || null,
        description: desc.trim(),
        sessionNo: sessionNo.trim() ? Number(sessionNo.trim()) : null,
        serviceId: serviceId || null,
        patientPackageId: patientPackageId || null,
      }),
    onSuccess: () => {
      setTooth("");
      setDesc("");
      setSessionNo("");
      setServiceId("");
      setPatientPackageId("");
      onChanged();
    },
  });

  const completeItem = useMutation({
    // #18868: completing an item inside a visit records which visit it was done in
    // (done_encounter_id). From the patient record there is no visit, and it stays null.
    mutationFn: (itemId: string) => dentalApi.completeItem(itemId, encounterId ?? null),
    onSuccess: onChanged,
  });

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold">{plan.title}</h4>
        <Badge variant="secondary">{ITEM_STATUS_LABEL[plan.status] ?? plan.status}</Badge>
      </div>

      {plan.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Chưa có hạng mục.</p>
      ) : (
        <ul className="space-y-1.5">
          {plan.items.map((it) => {
            const done = it.status === "COMPLETED";
            return (
              <li key={it.id} className="flex flex-wrap items-center gap-2 text-sm">
                {it.sessionNo != null && (
                  <span className="text-xs text-muted-foreground">Buổi {it.sessionNo}</span>
                )}
                {it.toothFdi && <Badge variant="outline">R{it.toothFdi}</Badge>}
                <span className={done ? "text-muted-foreground line-through" : ""}>
                  {it.description}
                </span>
                <Badge variant={done ? "secondary" : "outline"}>
                  {ITEM_STATUS_LABEL[it.status] ?? it.status}
                </Badge>
                {!done && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={completeItem.isPending}
                    onClick={() => completeItem.mutate(it.id)}
                  >
                    Đánh dấu hoàn tất
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {completeItem.isError && (
        <div className="mt-2">
          <ErrorState error={completeItem.error} inline />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2 border-t border-border pt-3">
        <div className="w-16">
          <label className="mb-1 block text-xs text-muted-foreground">Răng</label>
          <Input value={tooth} onChange={(e) => setTooth(e.target.value)} placeholder="36" />
        </div>
        <div className="w-20">
          <label className="mb-1 block text-xs text-muted-foreground">Buổi</label>
          <Input
            value={sessionNo}
            onChange={(e) => setSessionNo(e.target.value)}
            inputMode="numeric"
            placeholder="1"
          />
        </div>
        <div className="flex-1 min-w-40">
          <label className="mb-1 block text-xs text-muted-foreground">Hạng mục</label>
          <Input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="VD: Lấy tuỷ buồng"
          />
        </div>
        <div className="min-w-44">
          <label className="mb-1 block text-xs text-muted-foreground">Dịch vụ</label>
          <select
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            aria-label="Dịch vụ"
          >
            <option value="">— Không gắn —</option>
            {(services.data ?? []).map((sv) => (
              <option key={sv.id} value={sv.id}>
                {sv.name}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-44">
          <label className="mb-1 block text-xs text-muted-foreground">Trừ vào gói</label>
          <select
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value={patientPackageId}
            onChange={(e) => setPatientPackageId(e.target.value)}
            aria-label="Trừ vào gói"
            disabled={usablePackages.length === 0}
          >
            <option value="">— Không trừ gói —</option>
            {usablePackages.map((pk) => (
              <option key={pk.id} value={pk.id}>
                {pk.packageName} (còn {pk.sessionsRemaining})
              </option>
            ))}
          </select>
          {packages.data && usablePackages.length === 0 && (
            <p className="mt-1 text-xs text-muted-foreground">
              Khách chưa có gói nào còn dùng được.
            </p>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          disabled={!desc.trim() || addItem.isPending}
          onClick={() => addItem.mutate()}
        >
          {addItem.isPending ? "Đang thêm…" : "Thêm hạng mục"}
        </Button>
      </div>
      {addItem.isError && (
        <div className="mt-2">
          <ErrorState error={addItem.error} inline />
        </div>
      )}
    </div>
  );
}
